import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchOrders } from 'actions/OrderActions'
import { fetchExperienceList } from 'actions/ExperienceActions'
import { getExperienceListKey } from 'lib/experiences/experienceUtils'
import { haveOrdersBeenFetched } from 'lib/order/orderUtils'
import { selectLoggedIn } from 'selectors/accountSelectors'
import getUpcomingOrdersEligibleForLoungePassRedemption, { getPreQualifyingOrdersWithLoungeFilters } from 'checkout/selectors/loyalty/getUpcomingOrdersEligibleForLoungePassRedemption'
import LayoutContainer from 'components/Common/LayoutContainer'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import useTourV2Snapshot from 'hooks/TourV2/useTourV2Snapshot'
import useCruisePurchaseDetails from 'hooks/Cruise/useCruisePurchaseDetails'
import useFlightPurchaseDetails from 'hooks/Flights/useFlightPurchaseDetails'

const Section = styled.section`
  padding: ${rem(32)} 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
`

const Card = styled(ClickableLink)`
  display: flex;
  flex-direction: row;
  align-items: center;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  width: 100%;
  text-decoration: none;
  color: inherit;

  ${mediaQueryUp.tablet} {
    width: ${rem(400)};
  }

  &:hover {
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }
`

const CardImageContainer = styled.div`
  width: ${rem(102)};
  height: ${rem(100)};
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
`

const CarrierLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const CardContent = styled.div`
  flex: 1;
  padding: ${rem(12)};
  overflow: hidden;
`

interface CardProps {
  order: App.Order
  name: string
  location?: string
  imageId?: string
  image?: App.Image
  logoUrl?: string
}

function UpcomingOrderCard({ order, name, location, imageId, image, logoUrl }: CardProps) {
  return <Card to={`/account/my-escapes/order/${order.id}`}>
    <CardImageContainer>
      {imageId && <Image id={imageId} alt={name} fit="center" dpr={2} />}
      {!imageId && image && <Image image={image} alt={name} fit="center" dpr={2} />}
      {!imageId && !image && logoUrl && <CarrierLogo src={logoUrl} alt={name} />}
    </CardImageContainer>
    <CardContent>
      <BodyText variant="medium" weight="bold">{name}</BodyText>
      {location && <BodyText variant="small" colour="neutral-two">{location}</BodyText>}
    </CardContent>
  </Card>
}

function HotelUpcomingTripCard({ order }: { order: App.Order }) {
  const item = order.items[0]
  const city = item.offer.property?.geoData?.locality
  const countryCode = item.offer.property?.geoData?.countryCode
  const location = [city, countryCode].filter(Boolean).join(', ')
  return <UpcomingOrderCard
    order={order}
    name={item.offer.name}
    location={location}
    imageId={item.offer.image.id}
  />
}

function BedbankUpcomingTripCard({ order }: { order: App.Order }) {
  const item = order.bedbankItems[0]
  const city = item.offer.property?.address?.city
  const countryCode = item.offer.property?.address?.countryCode
  const location = [city, countryCode].filter(Boolean).join(', ')
  return <UpcomingOrderCard
    order={order}
    name={item.offer.name}
    location={location}
    image={item.offer.images?.[0]}
  />
}

function TourUpcomingTripCard({ order }: { order: App.Order }) {
  const item = order.tourItems[0]
  const [snapshot] = useTourV2Snapshot(item.reservationId, order.id)
  return <UpcomingOrderCard
    order={order}
    name={snapshot?.tourName ?? item.tour.title}
    location={item.tour.countriesVisited[0]}
    imageId={snapshot?.imageId ?? item.tour.images?.[0]?.id}
  />
}

function CruiseUpcomingTripCard({ order }: { order: App.Order }) {
  const { name, image, offerSnapshot } = useCruisePurchaseDetails(order, order.cruiseItems[0].bookingId)
  return <UpcomingOrderCard
    order={order}
    name={name ?? ''}
    location={offerSnapshot?.destinationName}
    imageId={image}
  />
}

function FlightUpcomingTripCard({ order }: { order: App.Order }) {
  const { flightsName, flightTypeLabel, carrierLogos } = useFlightPurchaseDetails(order)
  return <UpcomingOrderCard
    order={order}
    name={flightsName ?? ''}
    location={flightTypeLabel}
    logoUrl={carrierLogos[0]}
  />
}

function OrderUpcomingTripCard({ order }: { order: App.Order }) {
  if (order.tourItems.length) return <TourUpcomingTripCard order={order} />
  if (order.items.length) return <HotelUpcomingTripCard order={order} />
  if (order.bedbankItems.length) return <BedbankUpcomingTripCard order={order} />
  if (order.cruiseItems.length) return <CruiseUpcomingTripCard order={order} />
  if (order.flightItems.length) return <FlightUpcomingTripCard order={order} />
  return null
}

function LoungePassesUpcomingTripsSection() {
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const ordersFetched = useAppSelector(state => haveOrdersBeenFetched(state, 'upcoming'))
  const { filtersByOrderId } = useAppSelector(getPreQualifyingOrdersWithLoungeFilters)
  const eligibleOrders = useAppSelector(getUpcomingOrdersEligibleForLoungePassRedemption)

  const allFilters = useMemo(
    () => Object.values(filtersByOrderId).flat(),
    [filtersByOrderId],
  )

  // Stable set of already-dispatched filter keys — avoids re-dispatching when
  // experienceLists updates (which causes the selector to recompute and allFilters
  // to get a new reference, even when no new filters have been added).
  const dispatchedKeys = useRef(new Set<string>())

  useEffect(() => {
    if (!ordersFetched) {
      dispatch(fetchOrders())
    }
  }, [dispatch, isLoggedIn, ordersFetched])

  useEffect(() => {
    allFilters.forEach(filter => {
      const key = getExperienceListKey(filter)
      if (!dispatchedKeys.current.has(key)) {
        dispatchedKeys.current.add(key)
        dispatch(fetchExperienceList(filter))
      }
    })
  }, [dispatch, allFilters])

  if (!isLoggedIn || eligibleOrders.length === 0) return null

  return <Section>
    <LayoutContainer variant="large">
      <Group direction="vertical" gap={20}>
        <Heading variant="heading4" align="center">
          <FormattedMessage
            id="tufS4G-ln1KOJn3cbeSB"
            defaultMessage="Use your lounge passes on these upcoming trips"
            description="Heading for the upcoming qualifying trips section on the lounge passes benefit page"
          />
        </Heading>
        <CSSBreakpoint max="mobile">
          <CardList>
            {eligibleOrders.map(order => (
              <OrderUpcomingTripCard key={order.id} order={order} />
            ))}
          </CardList>
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          <CardCarousel>
            {eligibleOrders.map(order => (
              <OrderUpcomingTripCard key={order.id} order={order} />
            ))}
          </CardCarousel>
        </CSSBreakpoint>
      </Group>
    </LayoutContainer>
  </Section>
}

export default LoungePassesUpcomingTripsSection
